import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
	IMDDropDownOption,
	MDDropDownComponent,
	MDIconComponent,
	MDHeaderComponent,
	MDContentBoxComponent,
	MDToolTipDirective,
	MDTagComponent
} from '@melodic-ui/components';
import { LessonDetailStateService } from '../lesson-detail-state.service';
import { LessonDetailFormStateService } from '../lesson-detail-form-state.service';
import { AudioTranscribeInputComponent } from '@lb/components/audio-transcribe';
import { RichTextViewComponent } from '@lb/components/rich-text-view';

@Component({
	selector: 'lb-lesson-detail-homework',
	templateUrl: './lesson-detail-homework.component.html',
	styleUrl: './lesson-detail-homework.component.scss',
	imports: [
		CommonModule,
		FormsModule,
		MDIconComponent,
		MDHeaderComponent,
		MDDropDownComponent,
		MDContentBoxComponent,
		MDToolTipDirective,
		AudioTranscribeInputComponent,
		MDTagComponent,
		RichTextViewComponent
	],
	standalone: true
})
export class LessonDetailHomeworkComponent {
	readonly state = inject(LessonDetailStateService);
	private readonly formState = inject(LessonDetailFormStateService);

	lesson = this.state.lessonRequired;
	isReadOnly = this.formState.isReadOnly;
	fieldsDisabled = this.formState.fieldsDisabled;

	// The template is already fetched once for the whole lesson-detail page; reading it from state
	// avoids a second request for the same catalog and the load race that came with it.
	readonly categoryOptions = computed<IMDDropDownOption[]>(() =>
		(this.state.gradingTemplate()?.homeworkCategories ?? []).map((c) => ({
			value: c.homeworkCategoryID,
			label: c.categoryName
		}))
	);
	readonly selectedCategoryIDs = this.formState.selectedHomeworkCategoryIDs;
	readonly instructions = this.formState.homeworkInstructions;
	readonly homeworkLinks = this.formState.homeworkLinks;
	/**
	 * Names for the read-only display, taken from the saved lesson rather than the template catalog:
	 * the API already returns each selected category with its name, in catalog order. Learners only
	 * ever see this view, and resolving names through the catalog meant a category whose catalog
	 * entry didn't come back — a failed or slow fetch, a deactivated or re-versioned entry — rendered
	 * as nothing at all, with no sign anything was missing.
	 */
	readonly selectedCategoryNames = computed(() => (this.state.lesson()?.homework?.categories ?? []).map((c) => c.categoryName));

	isHomeworkInvalid = () => this.formState.isHomeworkInvalid();

	readonly showLinkForm = signal(false);
	readonly linkName = signal('');
	readonly linkUrl = signal('');

	onCategoryChange(value: unknown): void {
		if (Array.isArray(value)) {
			this.formState.updateSelectedHomeworkCategories(value);
		}
	}

	onInstructionsChange(value: string): void {
		this.formState.updateHomeworkInstructions(value);
	}

	toggleLinkForm(): void {
		this.showLinkForm.update((v) => !v);
		if (!this.showLinkForm()) {
			this.linkName.set('');
			this.linkUrl.set('');
		}
	}

	saveLink(): void {
		const name = this.linkName().trim();
		const url = this.linkUrl().trim();
		if (!name || !url) return;

		this.formState.addHomeworkLink({ name, url });
		this.showLinkForm.set(false);
		this.linkName.set('');
		this.linkUrl.set('');
	}

	removeLink(index: number): void {
		this.formState.removeHomeworkLink(index);
	}

	async pasteFromClipboard(): Promise<void> {
		try {
			const text = await navigator.clipboard.readText();
			if (text) {
				this.linkUrl.set(text.trim());
			}
		} catch {
			// Clipboard access denied or unavailable
		}
	}
}
